import {useCallback, useState, useEffect} from 'react';
import {useFarmSettings} from './useFarmSettings';
import {IFarm} from '../store/reducers/farm';
import {IOrganizationEntity, IPolygonEntity} from '@deep-planet/api-interfaces';
import {baseApiUrl} from '../config/const';
import axios from 'axios';

interface HarvestData {
  harvestDate: Date | null;
  targetBaume: number | null;
}

interface MaturityData {
  averageBaume: number | null;
  averagePH: number | null;
  averageTA: number | null;
  maturityImage: string | null;
  isCalibrated: boolean;
}

export const useHarvestSummary = (selectedOrganization: IOrganizationEntity, selectedFarm: IFarm, selectedPolygon: IPolygonEntity) => {
  const [harvestData, setHarvestData] = useState<HarvestData | null>(null);
  const [maturityData, setMaturityData] = useState<MaturityData | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>();

  const {farmSettings} = useFarmSettings(selectedFarm);
  // get maturity imagery along with the information of average pH, TA and Baume from the backend
  const fetchMaturityImages = useCallback(async () => {
    if (!selectedFarm?.id || !selectedPolygon?.id || !selectedOrganization?.id) return;
    setIsLoading(true);
    try {
      const {data} = await axios.get(`${baseApiUrl}/maturity_image/latest`, {
        params: {
          farmId: selectedFarm.id,
          polygonId: selectedPolygon.id,
          organizationId: selectedOrganization.id,
        },
      });
      setMaturityData({
        averageBaume: data.find((img: any) => img.type === 'BAUME')?.avgBaume ?? null,
        averagePH: data.find((img: any) => img.type === 'PH')?.avgBaume ?? null,
        averageTA: data.find((img: any) => img.type === 'TA')?.avgBaume ?? null,
        maturityImage: data.find((img: any) => img.type === 'BAUME')?.url ?? null,
        isCalibrated: !!data.thresholdLow,
      });
    } catch (error) {
      setMaturityData(null);
      console.error('Error fetching maturity images:', error);
    }
    setIsLoading(false);
  }, [selectedFarm, selectedPolygon, selectedOrganization]);

  // get predicted harvest date and customer target baume from the backend
  const fetchCustomerHarvestData = useCallback(async () => {
    if (!selectedFarm?.id || !selectedPolygon?.id || !selectedOrganization?.id) return;
    setIsLoading(true);
    try {
      setHarvestData(null);
      const {data} = await axios.get(`${baseApiUrl}/harvest-curves/customer-data`, {
        params: {
          farmId: selectedFarm.id,
          polygonId: selectedPolygon.id,
          organizationId: selectedOrganization.id,
        },
      });
      setHarvestData({
        harvestDate: data?.proposedHarvestDate ? new Date(data.proposedHarvestDate) : null,
        targetBaume: data?.targetBaume ?? null,
      });
    } catch (error) {
      console.error('Error fetching harvest data:', error);
    }
    setIsLoading(false);
  }, [selectedFarm, selectedOrganization, selectedPolygon]);

  // get predicted harvest date and customer target baume
  useEffect(() => {
    if (selectedFarm && selectedPolygon && selectedFarm?.ownerOrganization?.id === selectedOrganization?.id) {
      if (selectedFarm.polygons.find(({id}) => id === selectedPolygon.id)) {
        setHarvestData(null);
        fetchCustomerHarvestData();
      }
    }
  }, [fetchCustomerHarvestData, selectedFarm, selectedPolygon, selectedOrganization]);

  // get maturity imagery along with the information of average pH, TA and Baume
  // if image generation date is in the future, do not get maturity imagery and its details
  useEffect(() => {
    if (
      selectedFarm &&
      selectedPolygon &&
      selectedFarm?.ownerOrganization?.id === selectedOrganization?.id &&
      farmSettings?.imageGenerationDate &&
      farmSettings?.seasonEndDate &&
      new Date(farmSettings.imageGenerationDate).getTime() <= new Date().getTime() &&
      new Date(farmSettings.seasonEndDate).getTime() >= new Date().getTime()
    ) {
      setMaturityData(null);
      fetchMaturityImages();
    }
  }, [fetchMaturityImages, selectedPolygon, farmSettings, selectedFarm, selectedOrganization]);

  return {
    ...harvestData,
    ...maturityData,
    loadingHarvestData: isLoading,
    ...farmSettings,
  };
};
